/**
 * FilterBar – wiederverwendbare, konfigurierbare Filterleiste
 *
 * Features:
 * - Nutzer wählt selbst, welche Felder angezeigt werden
 * - Reihenfolge per Pfeil-Buttons anpassbar
 * - Operator pro Feld (ist / ist nicht / enthält / enthält nicht)
 * - AP-23: Die Feldauswahl liegt beim Benutzer auf dem Server (debounced), die
 *   zuletzt getippten Werte bleiben rein lokal — ein Datumsbereich vom Vormittag
 *   am Desktop gehört nicht auf den Laptop gespiegelt.
 * - Alle Felder kategorisiert (Kerndaten / Projektdaten / Zusatzfelder ZE / LV-Felder)
 *
 * Die Vorbelegung (`defaultFieldKeys`) legt jede Seite selbst fest — bewusst als
 * Pflicht-Prop, damit die Entscheidung beim Anlegen einer Liste fällt und nicht
 * aus einer seitenübergreifenden Konstante geerbt wird. Datumsfelder gehören nur
 * dorthin, wo die Zeile ein Ereignis zu einem Zeitpunkt ist (Arbeitszeiten ja,
 * Projektstammdaten nein). Regel: docs/pattern-auswertungsmaske.md.
 */

import { useState, useEffect, useRef, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useTerminology } from '@/lib/useTerminology'
import { Settings, Check, Lightbulb, Clock } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { HelpPopover } from '@/components/ui/HelpPopover'
import { isListHelpSeen, markListHelpSeen, onListHelpSeen } from '@/lib/listHelp'
import { LayoutDefaultActions } from '@/components/ui/LayoutDefaultActions'
import {
  loadUiSetting,
  markUiSettingApplied,
  saveUiSettingIfChanged,
  loadFilterLastValues,
  saveFilterLastValues,
  flushUiSettings,
} from '@/lib/ui-settings'
import type { FilterFieldDef, FilterOperator, ActiveFilter, SummaryFilterLayout } from '@/components/list/list.types';

// ─── Constants ────────────────────────────────────────────────────────────────

const OPERATOR_LABELS: Record<FilterOperator, string> = {
  is: 'ist',
  is_not: 'ist nicht',
  contains: 'enthält',
  not_contains: 'enthält nicht',
  gt: 'größer als',
  lt: 'kleiner als',
}

// ─── Types ────────────────────────────────────────────────────────────────────

export interface FilterBarProps {
  /** All fields that can be added to the filter bar */
  availableFields: FilterFieldDef[]
  /** Current active filter values (controlled) */
  value: Record<string, ActiveFilter>
  /** Called whenever a filter value or operator changes */
  onChange: (filters: Record<string, ActiveFilter>) => void
  /** Schlüssel der Einstellung (`filterbar_layout_<key>`) */
  layoutKey: string
  /**
   * Felder, die ein Benutzer ohne eigenes gespeichertes Layout sieht.
   * Pflicht — jede Liste entscheidet das für sich (siehe Kopfkommentar).
   */
  defaultFieldKeys: string[]
  /**
   * AP-35 Phase L — Werte, mit denen die Liste startet, **solange der Benutzer
   * hier noch nie selbst gefiltert hat** (Beispiel: Projektliste ohne
   * archivierte). Einmal geleert oder geändert, gilt seine Wahl — auch eine leere.
   */
  defaultValues?: Record<string, ActiveFilter>
  /**
   * AP-35 Nachtrag 6 — **alle Felder fest eingeblendet**, in der Reihenfolge von
   * `availableFields`. Zahnrad, Hilfe und Tipp-Balken entfallen, denn es gibt
   * nichts anzupassen (Stückliste: wenige Felder, die Leiste ist einklappbar).
   */
  festeFelder?: boolean
  /**
   * AP-40 6.4 (MD-9) — **Filter aus der Adresse**: Kommt man über eine Kachel eines Dashboards
   * (`/calculations?status=IN_PROGRESS&dueTo=…`), gelten genau diese Filter — vor den zuletzt
   * benutzten und vor der Vorgabe der Liste. Die Felder werden eingeblendet, damit man sieht, was
   * gefiltert ist. Erzeugt mit `filterAusAdresse`.
   */
  adresse?: Record<string, ActiveFilter> | null
}

/**
 * AP-40 6.4 — Filter aus den Parametern der Adresse lesen: `?status=IN_PROGRESS,SUBMITTED` wird
 * zum Filter „Status ist …" (erster Operator des Felds), `?status.not=DISCARDED` zu „ist nicht".
 * Nur Felder, die die Liste kennt; ohne passende Parameter `null`.
 */
export function filterAusAdresse(parameter: URLSearchParams, felder: FilterFieldDef[]): Record<string, ActiveFilter> | null {
  const ergebnis: Record<string, ActiveFilter> = {}
  for (const f of felder) {
    const ist = parameter.get(f.key)
    const nicht = parameter.get(`${f.key}.not`)
    if (ist !== null && ist !== '') ergebnis[f.key] = { fieldKey: f.key, operator: f.operators[0] ?? 'is', value: ist }
    else if (nicht !== null && nicht !== '' && f.operators.includes('is_not')) ergebnis[f.key] = { fieldKey: f.key, operator: 'is_not', value: nicht }
  }
  return Object.keys(ergebnis).length ? ergebnis : null
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Feldauswahl aus dem Cache (der nach dem Login vom Server gefüllt wird), die
 * zuletzt genutzten Werte aus dem rein lokalen Speicher.
 *
 * Es wird absichtlich NICHT gegen `availableFields` gefiltert: LV-Felder (und
 * andere asynchron nachgeladene) stehen beim Mount noch nicht darin. `shownFields`
 * verkraftet unbekannte Schlüssel über find+filter(Boolean) — gespeicherte
 * Schlüssel bleiben also unsichtbar, bis ihre Felddefinition eintrifft.
 */
function loadLayout(key: string, defaultFieldKeys: string[]): SummaryFilterLayout {
  const stored = loadUiSetting<{ fieldKeys?: string[] } | null>(`filterbar_layout_${key}`, null)
  const fieldKeys = stored?.fieldKeys?.length ? stored.fieldKeys : defaultFieldKeys
  return {
    fieldKeys,
    lastValues: loadFilterLastValues<SummaryFilterLayout['lastValues']>(key, {}),
  }
}

function defaultOperatorForField(field: FilterFieldDef): FilterOperator {
  return field.operators[0] ?? 'is'
}

// ─── Sub-component: searchable multi-select combobox ─────────────────────────

interface MultiSelectComboboxProps {
  options: { value: string; label: string }[]
  /** comma-separated selected IDs */
  value: string
  onChange: (value: string) => void
  inputCls: string
  /** true = Mehrfachauswahl (Checkboxen), false = Einfachauswahl (schließt nach Klick) */
  multiple?: boolean
}

function MultiSelectCombobox({ options, value, onChange, inputCls, multiple = true }: MultiSelectComboboxProps) {
  const [search, setSearch] = useState('')
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  const selectedIds = value ? value.split(',').filter(Boolean) : []

  const filtered = options.filter((o) =>
    o.label.toLowerCase().includes(search.toLowerCase()),
  )

  const toggle = (id: string) => {
    if (multiple) {
      const next = selectedIds.includes(id)
        ? selectedIds.filter((s) => s !== id)
        : [...selectedIds, id]
      onChange(next.join(','))
    } else {
      // Einfachauswahl: erneuter Klick auf den gewählten Eintrag hebt ihn auf,
      // sonst ersetzen. Danach Dropdown schließen und Suche zurücksetzen.
      onChange(selectedIds[0] === id ? '' : id)
      setOpen(false)
      setSearch('')
    }
  }

  // Close on outside click
  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
        setSearch('')
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const selectedLabels = selectedIds
    .map((id) => options.find((o) => o.value === id)?.label ?? id)

  return (
    <div className="relative" ref={containerRef}>
      {/* Input field */}
      <input
        type="text"
        value={open ? search : ''}
        placeholder={
          selectedIds.length === 0
            ? 'Alle'
            : selectedLabels.length === 1
            ? selectedLabels[0]
            : `${selectedIds.length} ausgewählt`
        }
        className={inputCls}
        onFocus={() => setOpen(true)}
        onClick={() => setOpen(true)}
        onChange={(e) => { setSearch(e.target.value); if (!open) setOpen(true) }}
        autoComplete="off"
      />

      {/* Dropdown */}
      {open && (
        <div className="absolute left-0 top-full z-50 mt-1 w-max min-w-full max-w-[28rem] max-h-60 overflow-y-auto rounded-md border border-border bg-background shadow-lg">
          {/* Einfachauswahl: expliziter „Alle"-Eintrag zum Zurücksetzen */}
          {!multiple && search === '' && (
            <button
              type="button"
              onClick={() => { onChange(''); setOpen(false) }}
              className={`flex w-full items-center gap-2.5 px-3 py-2 text-sm text-left hover:bg-muted ${
                selectedIds.length === 0 ? 'font-semibold text-primary' : 'text-muted-foreground'
              }`}
            >
              <span className="w-4 shrink-0" />
              <span className="truncate">Alle</span>
            </button>
          )}
          {filtered.length === 0 ? (
            <p className="px-3 py-2 text-sm text-muted-foreground">Keine Treffer</p>
          ) : (
            filtered.map((opt) => {
              const checked = selectedIds.includes(opt.value)
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => toggle(opt.value)}
                  className="flex w-full items-center gap-2.5 px-3 py-2 text-sm text-left hover:bg-muted"
                >
                  <span
                    className={`flex h-4 w-4 shrink-0 items-center justify-center border ${
                      multiple ? 'rounded' : 'rounded-full'
                    } ${
                      checked
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'border-border bg-background'
                    }`}
                  >
                    {checked && <Check size={10} strokeWidth={3} />}
                  </span>
                  <span className="truncate">{opt.label}</span>
                </button>
              )
            })
          )}
        </div>
      )}

      {/* Selected chips (shown when closed and ≥2 selected; nur Mehrfachauswahl) */}
      {multiple && !open && selectedIds.length >= 2 && (
        <div className="mt-1 flex flex-wrap gap-1">
          {selectedLabels.map((label, i) => (
            <span
              key={selectedIds[i]}
              className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-xs text-primary"
            >
              {label}
              <button
                type="button"
                onClick={() => toggle(selectedIds[i])}
                className="hover:text-destructive leading-none"
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  )
}

// ─── Sub-component: single filter field ──────────────────────────────────────

interface FilterFieldProps {
  fieldDef: FilterFieldDef
  filter: ActiveFilter
  onOperatorChange: (op: FilterOperator) => void
  onValueChange: (value: string) => void
  configMode: boolean
  onRemove: () => void
  isDragOver?: boolean
  onDragStart?: () => void
  onDragOver?: (e: React.DragEvent) => void
  onDrop?: () => void
  onDragEnd?: () => void
}

function FilterField({
  fieldDef,
  filter,
  onOperatorChange,
  onValueChange,
  configMode,
  onRemove,
  isDragOver,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
}: FilterFieldProps) {
  const { t } = useTranslation()
  const [operatorOpen, setOperatorOpen] = useState(false)
  const operatorRef = useRef<HTMLDivElement>(null)

  // Auto-correct stale operator from the stored layout: if the saved operator is no
  // longer in fieldDef.operators (e.g. field type changed from text → number),
  // silently reset to the first valid operator.
  useEffect(() => {
    if (filter.operator && !fieldDef.operators.includes(filter.operator)) {
      onOperatorChange(fieldDef.operators[0] ?? 'is')
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldDef.key, fieldDef.operators.join(',')])

  // Close operator dropdown on outside click
  useEffect(() => {
    if (!operatorOpen) return
    const handler = (e: MouseEvent) => {
      if (operatorRef.current && !operatorRef.current.contains(e.target as Node)) {
        setOperatorOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [operatorOpen])

  // Basisklassen OHNE Breite. `inputCls` (mit `w-full`) bleibt der Normalfall —
  // aber wo zwei Eingaben nebeneinander stehen, darf `w-full` nicht mitkommen:
  // Steht es zusammen mit einer festen Breite in derselben Klassenliste,
  // entscheidet die Reihenfolge im erzeugten Stylesheet, nicht die im Attribut.
  // Genau daran lag das übergroße Uhrzeitfeld, das ins Nachbarfeld lief.
  const inputBase =
    'rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring'
  const inputCls = `w-full ${inputBase}`

  // KA-18: Die Uhrzeit ist die Ausnahme, nicht der Normalfall — sie erscheint erst
  // auf Klick (Uhr-Symbol neben dem Feldnamen). Ein dauerhaft sichtbares zweites
  // Eingabefeld je Datumsfilter machte die Leiste unruhig und lief in die
  // Nachbarspalte. Ein einzelnes `datetime-local` scheidet aus: Der Browser gibt
  // gar keinen Wert heraus, solange die Uhrzeit fehlt — „nur ein Tag" wäre dann
  // nicht mehr mit einem Klick zu haben.
  const dayBoundary = fieldDef.defaultTime ?? '00:00'
  const enteredTime = filter.value.length > 10 ? filter.value.slice(11, 16) : ''
  const hasCustomTime = !!enteredTime && enteredTime !== dayBoundary

  // Sichtbar ist das Uhrzeitfeld, sobald es jemand aufgeklappt hat ODER ein Wert
  // eine Uhrzeit trägt. Das zweite ist keine Feinheit: Die zuletzt genutzten
  // Filterwerte werden erst nach dem Mount wiederhergestellt — mit reinem
  // State wäre die Uhrzeit dann aktiv, das Feld aber unsichtbar. Ein Filter, der
  // die Liste einengt, ohne dass man ihn sieht, ist eine Falle.
  const [timeToggled, setTimeToggled] = useState(false)
  const showTime = timeToggled || hasCustomTime

  // Show operator toggle only if more than one operator is available and field is not a date/datetime
  const showOperator = fieldDef.operators.length > 1 && fieldDef.type !== 'date' && fieldDef.type !== 'datetime'

  return (
    <div
      // Mit eingeblendeter Uhrzeit braucht das Feld mehr Platz. Ohne die
      // größere Mindestbreite quetscht das Zeitfeld das Datum im 160px-Kasten
      // zusammen, bis darin nichts mehr zu lesen ist (gemeldet am 6.9.2026).
      className={`flex flex-col gap-1 rounded-md transition-colors ${
        showTime && fieldDef.type === 'datetime' ? 'min-w-[17.5rem]' : 'min-w-[160px]'
      } ${configMode ? 'cursor-grab' : ''} ${
        isDragOver ? 'outline outline-2 outline-primary bg-primary/5' : ''
      }`}
      draggable={configMode}
      onDragStart={configMode ? onDragStart : undefined}
      onDragOver={configMode ? onDragOver : undefined}
      onDrop={configMode ? onDrop : undefined}
      onDragEnd={configMode ? onDragEnd : undefined}
    >
      {/* Label row – label left, operator (or remove button) right, all on one line */}
      <div className="flex h-4 items-center justify-between gap-1 px-3">
        <span className="text-xs font-medium text-foreground truncate">{fieldDef.label}</span>

        {configMode ? (
          <button
            type="button"
            onClick={onRemove}
            className="shrink-0 rounded p-0.5 text-destructive hover:text-destructive/80"
            title="Entfernen"
          >
            ✕
          </button>
        ) : fieldDef.type === 'datetime' ? (
          /* Uhrzeit ein-/ausblenden – an der Stelle, an der sonst der Operator sitzt */
          <button
            type="button"
            onClick={() => {
              if (showTime) {
                // Ausblenden setzt die Uhrzeit auf die Tagesgrenze zurück — sonst
                // bliebe eine unsichtbare Einschränkung stehen.
                if (filter.value) onValueChange(`${filter.value.slice(0, 10)}T${dayBoundary}`)
                setTimeToggled(false)
              } else {
                setTimeToggled(true)
              }
            }}
            className={`shrink-0 rounded p-0.5 leading-none ${
              hasCustomTime ? 'text-primary' : 'text-muted-foreground hover:text-foreground'
            }`}
            title={
              showTime
                ? t('filterBar.hideTime', {
                    defaultValue: 'Uhrzeit ausblenden (gilt wieder der ganze Tag)',
                  })
                : t('filterBar.showTime', { defaultValue: 'Uhrzeit angeben' })
            }
          >
            <Clock className="h-3.5 w-3.5" />
          </button>
        ) : showOperator ? (
          /* Operator toggle – right-aligned, same row as label */
          <div className="relative shrink-0" ref={operatorRef}>
            <button
              type="button"
              onClick={() => setOperatorOpen((v) => !v)}
              className="text-[10px] font-medium text-primary underline-offset-2 hover:underline leading-none"
            >
              {OPERATOR_LABELS[filter.operator]}
            </button>

            {operatorOpen && (
              <div className="absolute right-0 top-5 z-50 min-w-[140px] rounded-md border border-border bg-background shadow-lg">
                {fieldDef.operators.map((op) => (
                  <button
                    key={op}
                    type="button"
                    onClick={() => {
                      onOperatorChange(op)
                      setOperatorOpen(false)
                    }}
                    className={`flex w-full items-center gap-2 px-3 py-2 text-sm hover:bg-muted ${
                      filter.operator === op ? 'font-semibold text-primary' : 'text-foreground'
                    }`}
                  >
                    {filter.operator === op && <span>●</span>}
                    {filter.operator !== op && <span className="w-3" />}
                    {OPERATOR_LABELS[op]}
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : null}
      </div>

      {/* Input */}
      {fieldDef.type === 'date' && (
        <input
          type="date"
          value={filter.value}
          onChange={(e) => onValueChange(e.target.value)}
          className={inputCls}
        />
      )}

      {/*
        KA-18: Datum + optionale Uhrzeit. Vorher stand hier nur ein Datumsfeld,
        das die Vorgabezeit (00:00 / 23:59) fest anhängte — eine Uhrzeit war gar
        nicht eingebbar, obwohl der Feldtyp sie versprach. Bleibt die Uhrzeit
        leer, gilt weiterhin die Tagesgrenze und der Filter läuft über die
        timezone-neutrale Datumsspalte; wird eine gesetzt, filtert das Backend
        auf `startTime`.
      */}
      {fieldDef.type === 'datetime' && (() => {
        const datePart = filter.value ? filter.value.slice(0, 10) : ''
        return (
          <div className="flex items-center gap-1">
            <input
              type="date"
              value={datePart}
              onChange={(e) => {
                const date = e.target.value // YYYY-MM-DD
                if (!date) { onValueChange(''); return }
                onValueChange(date + 'T' + (enteredTime || dayBoundary))
              }}
              // `min-w-[8.5rem]`: Das Datum darf nicht unter seine Lesbarkeit
              // schrumpfen, wenn daneben die Uhrzeit steht.
              className={`${inputBase} min-w-[8.5rem] flex-1`}
            />
            {showTime && (
              <input
                type="time"
                // Die Tagesgrenze wird als „keine Uhrzeit" angezeigt (leeres Feld),
                // damit man sieht, ob die Uhrzeit den Filter tatsächlich einengt.
                value={hasCustomTime ? enteredTime : ''}
                disabled={!datePart}
                title={
                  datePart
                    ? t('filterBar.timeOptional', { defaultValue: 'Uhrzeit (leer = ganzer Tag)' })
                    : t('filterBar.pickDateFirst', { defaultValue: 'Erst ein Datum wählen' })
                }
                onChange={(e) => {
                  if (!datePart) return
                  onValueChange(datePart + 'T' + (e.target.value || dayBoundary))
                }}
                className={`${inputBase} w-[6.5rem] shrink-0 px-2 disabled:opacity-50`}
              />
            )}
          </div>
        )
      })()}

      {fieldDef.type === 'select' && filter.operator !== 'contains' && filter.operator !== 'not_contains' && (
        <MultiSelectCombobox
          options={fieldDef.options ?? []}
          value={filter.value}
          onChange={onValueChange}
          inputCls={inputCls}
          multiple={false}
        />
      )}

      {fieldDef.type === 'select-multi' && (
        <MultiSelectCombobox
          options={fieldDef.options ?? []}
          value={filter.value}
          onChange={onValueChange}
          inputCls={inputCls}
        />
      )}

      {(fieldDef.type === 'text' || (fieldDef.type === 'select' && (filter.operator === 'contains' || filter.operator === 'not_contains'))) && (
        <input
          type="text"
          value={filter.value}
          onChange={(e) => onValueChange(e.target.value)}
          placeholder="Suchen…"
          className={inputCls}
        />
      )}

      {fieldDef.type === 'number' && (filter.operator === 'gt' || filter.operator === 'lt' || filter.operator === 'is' || filter.operator === 'is_not') && (
        <input
          type="number"
          value={filter.value}
          onChange={(e) => onValueChange(e.target.value)}
          placeholder="Wert…"
          className={inputCls}
        />
      )}

      {fieldDef.type === 'number' && (filter.operator === 'contains' || filter.operator === 'not_contains') && (
        <input
          type="text"
          value={filter.value}
          onChange={(e) => onValueChange(e.target.value)}
          placeholder="Suchen…"
          className={inputCls}
        />
      )}

      {fieldDef.type === 'boolean' && (
        <select
          value={filter.value}
          onChange={(e) => onValueChange(e.target.value)}
          className={inputCls}
        >
          <option value="">Alle</option>
          <option value="true">Ja</option>
          <option value="false">Nein</option>
        </select>
      )}
    </div>
  )
}

// ─── Main component ───────────────────────────────────────────────────────────

export function FilterBar({ availableFields, value, onChange, layoutKey, defaultFieldKeys, defaultValues, festeFelder = false, adresse }: FilterBarProps) {
  const { t } = useTranslation()
  // AP-16: Die Kategorie heißt „Projektdaten" oder „Auftragsdaten" — je Mandant.
  const terms = useTerminology()

  const [layout, setLayout] = useState<SummaryFilterLayout>(() => {
    const l = loadLayout(layoutKey, defaultFieldKeys)
    // AP-40: Felder, nach denen die Adresse filtert, sind sichtbar.
    const fehlend = Object.keys(adresse ?? {}).filter((k) => !l.fieldKeys.includes(k))
    return fehlend.length ? { ...l, fieldKeys: [...l.fieldKeys, ...fehlend] } : l
  })
  const [configMode, setConfigMode] = useState(false)
  const [addDropdownOpen, setAddDropdownOpen] = useState(false)
  const addDropdownRef = useRef<HTMLDivElement>(null)

  // Erst-Hinweis „Tipp": sichtbar bis der Benutzer die Hilfe zum ersten Mal nutzt
  const [hintVisible, setHintVisible] = useState(() => !isListHelpSeen())
  useEffect(() => onListHelpSeen(() => setHintVisible(false)), [])

  // Restore last values on mount.
  // WICHTIG: Dieser Effekt MUSS vor den Sync-Effekten stehen.
  // Effekte laufen beim Mount in Deklarations-Reihenfolge — stünde der Sync-
  // Effekt davor, überschriebe er lastValues mit dem (anfangs leeren) `value`-
  // Prop, bevor dieser Restore die gespeicherten Werte lesen kann. Dann ginge
  // das Wiederherstellen der zuletzt genutzten Filter über einen Reload verloren.
  useEffect(() => {
    const saved = loadLayout(layoutKey, defaultFieldKeys)
    // `null` heißt „nie gespeichert" — nur dann greift die Vorgabe der Liste.
    const nieGefiltert = loadFilterLastValues<SummaryFilterLayout['lastValues'] | null>(layoutKey, null) === null
    if (adresse && Object.keys(adresse).length > 0) {
      onChange(adresse)
    } else if (nieGefiltert && defaultValues && Object.keys(defaultValues).length > 0) {
      onChange(defaultValues)
    } else if (Object.keys(saved.lastValues).length > 0) {
      onChange(saved.lastValues)
    }
    // Only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // AP-23: Der frühere gemeinsame Effekt schrieb bei JEDER Wertänderung das
  // gesamte Layout weg. Für den localStorage war das folgenlos, an einer API
  // wäre es ein Request je Tastendruck. Deshalb getrennt:

  // (a) Feldauswahl → Cache sofort, API debounced. `…IfChanged`, weil dieser
  // Effekt auch beim Mount läuft: Das bloße Öffnen einer Liste darf keine
  // eigene Einstellung anlegen, sonst griffe der Mandanten-Standard nie.
  useEffect(() => {
    saveUiSettingIfChanged(`filterbar_layout_${layoutKey}`, { fieldKeys: layout.fieldKeys })
  }, [layout.fieldKeys, layoutKey])

  // (b) Zuletzt getippte Werte → ausschließlich lokal, bei jeder Änderung.
  useEffect(() => {
    saveFilterLastValues(layoutKey, value)
  }, [value, layoutKey])

  // Beim Verlassen der Seite nicht auf den Debounce warten — sonst ginge eine
  // Änderung verloren, die direkt vor einem Seitenwechsel gemacht wurde.
  useEffect(() => () => flushUiSettings(), [])

  // Close add-dropdown on outside click
  useEffect(() => {
    if (!addDropdownOpen) return
    const handler = (e: MouseEvent) => {
      if (addDropdownRef.current && !addDropdownRef.current.contains(e.target as Node)) {
        setAddDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [addDropdownOpen])

  // Count active (non-empty) filters for the badge
  const activeCount = Object.values(value).filter((f) => f.value !== '').length

  // Fields currently shown
  const shownFields = festeFelder
    ? availableFields
    : (layout.fieldKeys.map((k) => availableFields.find((f) => f.key === k)).filter(Boolean) as FilterFieldDef[])

  // Fields not yet shown (available to add)
  const availableToAdd = availableFields.filter((f) => !layout.fieldKeys.includes(f.key))

  // Group available-to-add fields by category
  const categoryLabels: Record<string, string> = {
    core: t('filterBar.categoryCoreData', { defaultValue: 'Kerndaten' }),
    project: terms.data,
    customField: t('filterBar.categoryCF', { defaultValue: 'Zusatzfelder Zeiterfassung' }),
    lohnverrechnung: t('filterBar.categoryLV', { defaultValue: 'Felder Lohnverrechnung' }),
  }

  const grouped = availableToAdd.reduce<Record<string, FilterFieldDef[]>>((acc, f) => {
    const cat = f.category
    if (!acc[cat]) acc[cat] = []
    acc[cat].push(f)
    return acc
  }, {})

  // ── Handlers ────────────────────────────────────────────────────────────────

  const handleValueChange = useCallback(
    (fieldKey: string, newValue: string) => {
      const fieldDef = availableFields.find((f) => f.key === fieldKey)
      if (!fieldDef) return
      const current = value[fieldKey]
      onChange({
        ...value,
        [fieldKey]: {
          fieldKey,
          operator: current?.operator ?? defaultOperatorForField(fieldDef),
          value: newValue,
        },
      })
    },
    [value, onChange, availableFields]
  )

  const handleOperatorChange = useCallback(
    (fieldKey: string, op: FilterOperator) => {
      const current = value[fieldKey]
      // current kann undefined sein wenn der User zuerst den Operator ändert,
      // bevor er einen Wert eintippt – in diesem Fall ein leeres ActiveFilter anlegen
      // damit der Operator nicht still ignoriert wird.
      onChange({
        ...value,
        [fieldKey]: current
          ? { ...current, operator: op }
          : { fieldKey, operator: op, value: '' },
      })
    },
    [value, onChange]
  )

  const handleRemoveField = useCallback(
    (fieldKey: string) => {
      setLayout((prev) => ({
        ...prev,
        fieldKeys: prev.fieldKeys.filter((k) => k !== fieldKey),
      }))
      // Clear the filter value
      const next = { ...value }
      delete next[fieldKey]
      onChange(next)
    },
    [value, onChange]
  )

  const handleAddField = useCallback(
    (fieldKey: string) => {
      setLayout((prev) => ({
        ...prev,
        fieldKeys: [...prev.fieldKeys, fieldKey],
      }))
      setAddDropdownOpen(false)
    },
    []
  )

  // DnD state
  const draggedKeyRef = useRef<string | null>(null)
  const [dragOverKey, setDragOverKey] = useState<string | null>(null)

  const handleDragStart = useCallback((key: string) => { draggedKeyRef.current = key }, [])
  const handleDragOver = useCallback((e: React.DragEvent, key: string) => {
    e.preventDefault()
    if (draggedKeyRef.current && draggedKeyRef.current !== key) setDragOverKey(key)
  }, [])
  const handleDrop = useCallback((targetKey: string) => {
    const srcKey = draggedKeyRef.current
    if (!srcKey || srcKey === targetKey) { setDragOverKey(null); return }
    setLayout((prev) => {
      const keys = [...prev.fieldKeys]
      const from = keys.indexOf(srcKey)
      const to = keys.indexOf(targetKey)
      if (from < 0 || to < 0) return prev
      keys.splice(from, 1)
      keys.splice(to, 0, srcKey)
      return { ...prev, fieldKeys: keys }
    })
    draggedKeyRef.current = null
    setDragOverKey(null)
  }, [])
  const handleDragEnd = useCallback(() => { draggedKeyRef.current = null; setDragOverKey(null) }, [])

  const handleReset = useCallback(() => {
    onChange({})
  }, [onChange])

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="relative rounded-lg border border-border bg-card p-4">
      {!festeFelder && (
      <>
      {/* Gear icon – absolute top-right of the card frame */}
      <button
        type="button"
        onClick={() => setConfigMode((v) => !v)}
        className={`absolute right-1.5 top-1.5 flex h-6 w-6 items-center justify-center rounded-md transition-colors ${
          configMode
            ? 'bg-primary text-primary-foreground'
            : 'text-muted-foreground hover:bg-muted hover:text-foreground'
        }`}
        title={
          configMode
            ? t('filterBar.doneConfig', { defaultValue: 'Fertig' })
            : t('filterBar.configure', { defaultValue: 'Filter anpassen' })
        }
      >
        {configMode ? <Check className="h-4 w-4" /> : <Settings className="h-4 w-4" />}
      </button>

      {/* Hilfe „?" – links neben dem Zahnrad */}
      <HelpPopover topic="filter" className="absolute right-8 top-1.5" />

      {/* Erst-Hinweis-Balken (verschwindet dauerhaft beim ersten Hilfe-Klick) */}
      {hintVisible && (
        <div className="mb-3 flex items-start gap-2 rounded-md border border-primary/30 bg-primary/5 px-3 py-2 pr-8 text-xs text-foreground">
          <Lightbulb className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
          <span className="flex-1">
            {t('listHelp.hintBanner', {
              defaultValue:
                'Tipp: Du kannst Suchfelder und Spalten selbst anpassen – klick auf das ⚙️ oder das ? oben rechts.',
            })}
          </span>
          <button
            type="button"
            onClick={() => { markListHelpSeen(); setHintVisible(false) }}
            className="shrink-0 rounded px-1.5 py-0.5 font-medium text-primary hover:bg-primary/10"
          >
            {t('listHelp.hintDismiss', { defaultValue: 'Verstanden' })}
          </button>
        </div>
      )}
      </>
      )}

      <div className="flex flex-wrap items-start gap-4">
        {/* Filter fields */}
        {shownFields.map((fieldDef) => {
          const current = value[fieldDef.key] ?? {
            fieldKey: fieldDef.key,
            operator: defaultOperatorForField(fieldDef),
            value: '',
          }
          return (
            <FilterField
              key={fieldDef.key}
              fieldDef={fieldDef}
              filter={current}
              onOperatorChange={(op) => handleOperatorChange(fieldDef.key, op)}
              onValueChange={(v) => handleValueChange(fieldDef.key, v)}
              configMode={configMode}
              onRemove={() => handleRemoveField(fieldDef.key)}
              isDragOver={dragOverKey === fieldDef.key}
              onDragStart={() => handleDragStart(fieldDef.key)}
              onDragOver={(e) => handleDragOver(e, fieldDef.key)}
              onDrop={() => handleDrop(fieldDef.key)}
              onDragEnd={handleDragEnd}
            />
          )
        })}

        {/* Config-mode: Add field dropdown */}
        {configMode && availableToAdd.length > 0 && (
          <div className="relative self-end" ref={addDropdownRef}>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setAddDropdownOpen((v) => !v)}
              className="border-dashed border-border text-muted-foreground hover:text-foreground"
            >
              + {t('filterBar.addField', { defaultValue: 'Feld hinzufügen' })}
            </Button>

            {addDropdownOpen && (
              <div className="absolute top-10 left-0 z-50 max-h-72 min-w-[220px] overflow-y-auto rounded-md border border-border bg-background shadow-lg">
                {Object.entries(grouped).map(([cat, fields]) => (
                  <div key={cat}>
                    <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                      {categoryLabels[cat] ?? cat}
                    </div>
                    {fields.map((f) => (
                      <button
                        key={f.key}
                        type="button"
                        onClick={() => handleAddField(f.key)}
                        className="flex w-full items-center px-4 py-2 text-sm text-foreground hover:bg-muted"
                      >
                        {f.label}
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* AP-23 Phase D: Zurücksetzen / als Mandanten-Standard speichern */}
        {configMode && (
          <div className="w-full border-t border-border pt-3">
            <LayoutDefaultActions
              settingKey={`filterbar_layout_${layoutKey}`}
              currentValue={{ fieldKeys: layout.fieldKeys }}
              onReset={(value) => {
                const std = (value as { fieldKeys?: string[] } | null)?.fieldKeys
                const next = std?.length ? std : defaultFieldKeys
                // Der Sync-Effekt läuft gleich danach — ihm sagen, dass dieser
                // Stand bereits gilt, sonst landet er sofort wieder als eigene
                // Einstellung auf dem Server.
                markUiSettingApplied(`filterbar_layout_${layoutKey}`, { fieldKeys: next })
                setLayout((prev) => ({ ...prev, fieldKeys: next }))
              }}
            />
          </div>
        )}

        {/* Active filter count badge (only when not in config mode) */}
        {!configMode && activeCount > 0 && (
          <div className="ml-auto self-end">
            <button
              type="button"
              onClick={handleReset}
              className="rounded-full bg-primary/10 px-2.5 py-1 text-xs font-medium text-primary hover:bg-primary/20"
              title={t('filterBar.resetAll', { defaultValue: 'Alle Filter zurücksetzen' })}
            >
              {activeCount} {t('filterBar.activeFilters', { defaultValue: 'aktiv' })} ✕
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
